import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarPlus, Copy, KeyRound, Plus, RefreshCw, Send, Trash2 } from 'lucide-react';
import { CALENDAR_FEED_DATES, type ApiTokenInfo, type CalendarFeed, type CalendarFeedCreated, type CalendarFeedDates, type WebhookDeliveryInfo, type WebhookInfo } from '@kaba/shared';
import { api } from '../lib/api';
import { queryClient } from '../lib/board';
import { toast } from '../lib/toast';
import { Button, cx, Sheet } from './ui/ui';

const inputCls = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-10 sm:text-sm';
const fmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
const when = (iso: string | null, never = 'nie') => (iso ? fmt.format(new Date(iso)) : never);

/** Geheimnis einmalig anzeigen: markierbares Feld + Kopieren */
export function SecretBox({ label, value, hint, extra }: { label: string; value: string; hint?: string; extra?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-accent bg-accent-soft/40 p-3">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex gap-2">
        <input readOnly value={value} aria-label={label} onFocus={(e) => e.target.select()} className={cx(inputCls, 'min-w-0 flex-1 font-mono text-xs sm:text-xs')} />
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              toast.info('Kopiert.');
            } catch {
              toast.info('Kopieren nicht möglich – bitte markieren und kopieren.');
            }
          }}
        >
          <Copy size={16} /> Kopieren
        </Button>
      </div>
      {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
      {extra}
    </div>
  );
}

/* ---------- Kalender-Abo ---------- */

const feedsKey = ['me', 'feeds'];

export function CalendarFeedPanel({ boardId }: { boardId: string | null }) {
  const { data: feeds } = useQuery({ queryKey: feedsKey, queryFn: () => api.get<CalendarFeed[]>('/api/me/feeds') });
  const [created, setCreated] = useState<CalendarFeedCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const feed = feeds?.find((f) => f.boardId === boardId) ?? null;
  const [draftDates, setDraftDates] = useState<CalendarFeedDates>('due');
  const dates = feed?.dates ?? draftDates;

  /** 0.73: Inhalt wählen – bei bestehendem Abo sofort gespeichert, der Link bleibt */
  const changeDates = async (d: CalendarFeedDates) => {
    if (!feed) return setDraftDates(d);
    const before = feeds;
    queryClient.setQueryData<CalendarFeed[]>(feedsKey, (list) => list?.map((f) => (f.id === feed.id ? { ...f, dates: d } : f)));
    try {
      await api.patch(`/api/me/feeds/${feed.id}`, { dates: d });
      void queryClient.invalidateQueries({ queryKey: feedsKey });
      toast.info('Gespeichert. Kalender-Apps übernehmen das beim nächsten Abruf.');
    } catch (err) {
      queryClient.setQueryData(feedsKey, before);
      toast.error((err as Error).message);
    }
  };

  const create = async () => {
    if (feed && !window.confirm('Neuen Link erzeugen? Der bisherige Link hört sofort auf zu funktionieren – Kalender damit müssen neu abonniert werden.')) return;
    setBusy(true);
    try {
      setCreated(await api.post<CalendarFeedCreated>('/api/me/feeds', { boardId, dates }));
      void queryClient.invalidateQueries({ queryKey: feedsKey });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-[var(--ink-soft)]">
        {boardId
          ? 'Alle Karten dieses Boards mit Datum als Kalender-Abo – z. B. in Outlook, Google Kalender oder auf dem Handy.'
          : 'Alle Karten mit Datum, für die du zuständig bist – über alle Boards. Kalender-Apps aktualisieren das Abo etwa stündlich.'}{' '}
        Erledigte Karten bleiben 30 Tage mit ✓ sichtbar.
      </p>
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-sm font-medium">Was soll im Kalender stehen?</legend>
        {CALENDAR_FEED_DATES.map((o) => (
          <label key={o.value} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-1 py-1.5 text-sm hover:bg-black/[0.03] dark:hover:bg-white/5">
            <input
              type="radio"
              name={`feed-dates-${boardId ?? 'me'}`}
              className="mt-0.5 size-5 shrink-0 accent-[var(--color-accent)]"
              checked={dates === o.value}
              onChange={() => void changeDates(o.value)}
            />
            <span>
              <span className="block font-medium">{o.label}</span>
              <span className="block text-xs text-[var(--ink-soft)]">{o.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {created ? (
        <SecretBox
          label="Abo-Link"
          value={created.url}
          hint="Wer diesen Link kennt, sieht die Fristen. Er wird nur jetzt angezeigt; du kannst jederzeit einen neuen erzeugen."
          extra={
            <a href={created.webcalUrl} className="inline-flex min-h-11 items-center gap-2 self-start text-sm font-medium text-accent-fg underline-offset-2 hover:underline">
              <CalendarPlus size={16} /> In Kalender-App öffnen
            </a>
          }
        />
      ) : feed ? (
        <p className="text-sm">
          Abo aktiv seit {when(feed.createdAt)} · zuletzt abgerufen: {when(feed.lastUsedAt, 'noch nie')}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant={feed ? undefined : 'primary'} disabled={busy} onClick={create}>
          {feed ? <RefreshCw size={16} /> : <CalendarPlus size={16} />} {feed ? 'Neuen Link erzeugen' : 'Abo-Link erzeugen'}
        </Button>
        {feed && (
          <Button
            onClick={async () => {
              if (!window.confirm('Abo beenden? Der Link funktioniert danach nicht mehr.')) return;
              await api.del(`/api/me/feeds/${feed.id}`);
              setCreated(null);
              void queryClient.invalidateQueries({ queryKey: feedsKey });
            }}
          >
            <Trash2 size={16} /> Abo beenden
          </Button>
        )}
      </div>
    </div>
  );
}

export function CalendarFeedSheet({ boardId, open, onClose }: { boardId: string; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Kalender abonnieren" side="center">
      <div className="p-4">{open && <CalendarFeedPanel boardId={boardId} />}</div>
    </Sheet>
  );
}

/* ---------- API-Tokens ---------- */

const tokensKey = ['me', 'tokens'];

export function ApiTokensSection() {
  const { data: tokens } = useQuery({ queryKey: tokensKey, queryFn: () => api.get<ApiTokenInfo[]>('/api/me/tokens') });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'write'>('read');
  const [expires, setExpires] = useState('90');
  const [fresh, setFresh] = useState<string | null>(null);

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">API-Zugriff</h2>
      <p className="text-sm text-[var(--ink-soft)]">
        Persönliche Tokens für Skripte und Werkzeuge wie n8n. Ein Token darf genau das, was du darfst – „nur lesen“ verhindert jede Änderung. Senden
        als <code className="rounded bg-[var(--column)] px-1">Authorization: Bearer …</code>, Beschreibung unter <code className="rounded bg-[var(--column)] px-1">docs/api.md</code>.
      </p>
      {fresh && <SecretBox label="Neuer Token" value={fresh} hint="Nur jetzt sichtbar. Sicher aufbewahren – wer ihn hat, handelt in deinem Namen." />}
      {adding ? (
        <form
          className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-3"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const r = await api.post<{ token: string }>('/api/me/tokens', {
                name: name.trim(),
                scope,
                expiresInDays: expires ? Number(expires) : null,
              });
              setFresh(r.token);
              setAdding(false);
              setName('');
              void queryClient.invalidateQueries({ queryKey: tokensKey });
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
        >
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Wofür?
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} placeholder="z. B. n8n Wochenbericht" autoFocus />
          </label>
          <fieldset className="flex flex-wrap gap-2">
            <legend className="mb-1 text-sm font-medium">Rechte</legend>
            {(
              [
                ['read', 'Nur lesen'],
                ['write', 'Lesen und ändern'],
              ] as const
            ).map(([v, l]) => (
              <label key={v} className={cx('flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm', scope === v ? 'border-accent bg-accent-soft/50' : 'border-[var(--line)]')}>
                <input type="radio" name="tok-scope" checked={scope === v} onChange={() => setScope(v)} className="size-4 accent-[var(--color-accent)]" />
                {l}
              </label>
            ))}
          </fieldset>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Gültig
            <select className={inputCls} value={expires} onChange={(e) => setExpires(e.target.value)}>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="365">1 Jahr</option>
              <option value="">unbegrenzt</option>
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => setAdding(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              Token erzeugen
            </Button>
          </div>
        </form>
      ) : (
        <Button className="self-start" onClick={() => (setFresh(null), setAdding(true))}>
          <KeyRound size={16} /> Neuer Token
        </Button>
      )}
      {!!tokens?.length && (
        <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
          {tokens.map((t) => {
            const expired = t.expiresAt && new Date(t.expiresAt) < new Date();
            return (
              <li key={t.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {t.name} <span className="font-mono text-xs font-normal text-[var(--ink-soft)]">{t.prefix}…</span>
                  </p>
                  <p className="truncate text-xs text-[var(--ink-soft)]">
                    {t.scope === 'read' ? 'nur lesen' : 'lesen und ändern'} · zuletzt benutzt: {when(t.lastUsedAt)} ·{' '}
                    {expired ? <span className="text-rose-600">abgelaufen</span> : t.expiresAt ? `gültig bis ${when(t.expiresAt)}` : 'ohne Ablauf'}
                  </p>
                </div>
                <button
                  aria-label={`Token „${t.name}“ widerrufen`}
                  className="inline-flex size-10 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                  onClick={async () => {
                    if (!window.confirm(`Token „${t.name}“ widerrufen? Skripte damit funktionieren sofort nicht mehr.`)) return;
                    await api.del(`/api/me/tokens/${t.id}`);
                    void queryClient.invalidateQueries({ queryKey: tokensKey });
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ---------- Webhooks ---------- */

const GROUPS: [string, string][] = [
  ['card', 'Karten'],
  ['comment', 'Kommentare'],
  ['column', 'Spalten'],
  ['members', 'Mitglieder'],
  ['board', 'Board, Labels, Felder'],
];

export function WebhooksSheet({ boardId, open, onClose }: { boardId: string; open: boolean; onClose: () => void }) {
  const key = ['webhooks', boardId];
  const { data: hooks } = useQuery({ queryKey: key, queryFn: () => api.get<WebhookInfo[]>(`/api/boards/${boardId}/webhooks`), enabled: open, refetchInterval: open ? 15_000 : false });
  const [editing, setEditing] = useState<WebhookInfo | 'new' | null>(null);
  const [log, setLog] = useState<WebhookInfo | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const close = () => {
    setEditing(null);
    setLog(null);
    setSecret(null);
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && close()} title={editing ? (editing === 'new' ? 'Neuer Webhook' : 'Webhook bearbeiten') : log ? 'Zustellungen' : 'Webhooks'}>
      {editing ? (
        <WebhookEditor
          boardId={boardId}
          initial={editing === 'new' ? null : editing}
          onDone={(s) => {
            setEditing(null);
            if (s) setSecret(s);
            void refresh();
          }}
        />
      ) : log ? (
        <DeliveryLog hook={log} onBack={() => setLog(null)} />
      ) : (
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-[var(--ink-soft)]">
            KaBa schickt Änderungen an diesem Board als JSON per POST an eine Adresse – etwa an n8n, Zapier oder ein eigenes Skript. Jede Lieferung ist
            mit einem Schlüssel signiert (Header <code>X-KaBa-Signature</code>).
          </p>
          {secret && <SecretBox label="Signaturschlüssel" value={secret} hint="Nur jetzt sichtbar. Im Empfänger hinterlegen, um Lieferungen zu prüfen." />}
          <Button variant="primary" className="self-start" onClick={() => (setSecret(null), setEditing('new'))}>
            <Plus size={16} /> Neuer Webhook
          </Button>
          {hooks && !hooks.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine Webhooks.</p>}
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
            {hooks?.map((h) => (
              <li key={h.id} className="flex flex-col gap-2 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <span
                    className={cx('mt-1.5 size-2.5 shrink-0 rounded-full', !h.enabled ? 'bg-[var(--ink-soft)]' : h.lastError ? 'bg-rose-500' : 'bg-emerald-500')}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-sm">{h.url}</p>
                    <p className="text-xs text-[var(--ink-soft)]">
                      {h.events.length ? GROUPS.filter(([g]) => h.events.includes(g)).map(([, l]) => l).join(', ') : 'alle Ereignisse'} ·{' '}
                      {!h.enabled ? 'pausiert' : h.lastDeliveryAt ? `zuletzt ${when(h.lastDeliveryAt)}` : 'noch nichts gesendet'}
                    </p>
                    {h.lastError && (
                      <p className="text-xs text-rose-600">
                        {h.lastError}
                        {h.enabled && h.nextAttemptAt ? ` · nächster Versuch ${when(h.nextAttemptAt)}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    onClick={async () => {
                      const r = await api.post<{ status: number | null; error: string | null }>(`/api/webhooks/${h.id}/test`).catch((e: Error) => ({ status: null, error: e.message }));
                      if (r.error) toast.error(`Test fehlgeschlagen: ${r.error}`);
                      else toast.info(`Test angekommen (Antwort ${r.status}).`);
                      void queryClient.invalidateQueries({ queryKey: ['webhook-log', h.id] });
                    }}
                  >
                    <Send size={15} /> Testen
                  </Button>
                  <Button onClick={() => setLog(h)}>Zustellungen</Button>
                  <Button
                    onClick={async () => {
                      await api.patch(`/api/webhooks/${h.id}`, { enabled: !h.enabled });
                      void refresh();
                    }}
                  >
                    {h.enabled ? 'Pausieren' : 'Fortsetzen'}
                  </Button>
                  <Button onClick={() => (setSecret(null), setEditing(h))}>Bearbeiten</Button>
                  <Button
                    aria-label="Webhook löschen"
                    onClick={async () => {
                      if (!window.confirm(`Webhook an ${h.url} löschen?`)) return;
                      await api.del(`/api/webhooks/${h.id}`);
                      void refresh();
                    }}
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}

function WebhookEditor({ boardId, initial, onDone }: { boardId: string; initial: WebhookInfo | null; onDone: (secret?: string) => void }) {
  const [url, setUrl] = useState(initial?.url ?? '');
  const [events, setEvents] = useState<string[]>(initial?.events ?? []);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="flex flex-col gap-4 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          if (initial) {
            await api.patch(`/api/webhooks/${initial.id}`, { url: url.trim(), events });
            onDone();
          } else {
            const r = await api.post<{ secret: string }>(`/api/boards/${boardId}/webhooks`, { url: url.trim(), events, enabled: true });
            onDone(r.secret);
          }
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <button type="button" onClick={() => onDone()} className="inline-flex min-h-9 items-center gap-1 self-start text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
        <ArrowLeft size={16} /> Zurück
      </button>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Adresse (URL)
        <input className={inputCls} type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://n8n.example.com/webhook/…" inputMode="url" />
      </label>
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-sm font-medium">Ereignisse (nichts gewählt = alle)</legend>
        {GROUPS.map(([g, l]) => (
          <label key={g} className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={events.includes(g)}
              onChange={(e) => setEvents((x) => (e.target.checked ? [...x, g] : x.filter((y) => y !== g)))}
              className="size-5 accent-[var(--color-accent)]"
            />
            {l}
          </label>
        ))}
      </fieldset>
      {initial && (
        <Button
          type="button"
          className="self-start"
          onClick={async () => {
            if (!window.confirm('Neuen Signaturschlüssel erzeugen? Der Empfänger muss danach den neuen Schlüssel verwenden.')) return;
            const r = await api.post<{ secret: string }>(`/api/webhooks/${initial.id}/secret`);
            onDone(r.secret);
          }}
        >
          <RefreshCw size={16} /> Neuer Schlüssel
        </Button>
      )}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={busy || !url.trim()}>
          {initial ? 'Speichern' : 'Anlegen'}
        </Button>
      </div>
    </form>
  );
}

function DeliveryLog({ hook, onBack }: { hook: WebhookInfo; onBack: () => void }) {
  const { data } = useQuery({ queryKey: ['webhook-log', hook.id], queryFn: () => api.get<WebhookDeliveryInfo[]>(`/api/webhooks/${hook.id}/deliveries`) });
  return (
    <div className="flex flex-col gap-3 p-4">
      <button type="button" onClick={onBack} className="inline-flex min-h-9 items-center gap-1 self-start text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
        <ArrowLeft size={16} /> Zurück
      </button>
      <p className="truncate font-mono text-sm">{hook.url}</p>
      {data && !data.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine Zustellungen.</p>}
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)] text-sm">
        {data?.map((d) => (
          <li key={d.id} className="flex flex-col px-3 py-2">
            <span className="flex items-center gap-2">
              <span className={cx('rounded px-1.5 font-mono text-xs', d.error ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200')}>
                {d.status ?? '–'}
              </span>
              <span className="font-mono text-xs">{d.eventType}</span>
              <span className="ml-auto text-xs text-[var(--ink-soft)]">
                {when(d.at)}
                {d.durationMs != null ? ` · ${d.durationMs} ms` : ''}
              </span>
            </span>
            {d.error && <span className="text-xs text-rose-600">{d.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

